import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import AbuserCard from "../components/AbuserCard";
import RegistryFilters from "../components/RegistryFilters";
import SpeciesFilterRow from "../components/SpeciesFilterRow";
import CrimeTypeFilterRow from "../components/CrimeTypeFilterRow";
import ReptileProtectionGap from "../components/ReptileProtectionGap";
import NearMeSearch from "../components/NearMeSearch";
import SponsorSidebar from "../components/SponsorSidebar";
import NotifyAlertsButton from "../components/NotifyAlertsButton";
import ShareRegistryButton from "../components/ShareRegistryButton";
import ConvictedCarousel from "../components/ConvictedCarousel";
import RescuePartnersStrip from "../components/RescuePartnersStrip";
import ActiveCasesStrip from "../components/ActiveCasesStrip";
import CashAppZelleTip from "../components/CashAppZelleTip";
import SubscribeForm from "../components/SubscribeForm";
import SEO, { homeJsonLd, orgJsonLd } from "../components/SEO";
import { ArrowRight, ShieldAlert, FileWarning, MapPinned, Activity, Target, Heart, PawPrint, Search } from "lucide-react";
import { Link } from "react-router-dom";
import HomeRescueBanner from "../components/HomeRescueBanner";
import HomeCampaignSection from "../components/HomeCampaignSection";

const HERO_IMG = "/hero-paws-2026.jpg";

const DEFAULT_FILTERS = {
  q: "",
  state: "",
  city: "",
  crime_type: "",
  year: "",
  near_city: "",
  near_state: "",
  near_lat: undefined,
  near_lng: undefined,
  radius_mi: 50,
  disposition: "convicted_registry",  // default view is the Convicted Registry
  page: 1,
};

export default function Home() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [origin, setOrigin] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      });
      // Only pass radius_mi if near_* is active
      if (!params.near_city && params.near_lat === undefined) {
        delete params.radius_mi;
        delete params.near_state;
      }
      const { data } = await api.get("/abusers", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setOrigin(data.origin || null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    api.get("/abusers/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const onSearch = () => fetchData();

  const stateStats = stats?.by_state?.slice(0, 6) || [];
  const radiusActive = filters.near_city || filters.near_lat !== undefined;

  return (
    <div data-testid="home-page">
      <SEO
        title="U.S. Convicted Animal Abuser Registry"
        description="Free, public registry of convicted animal abusers across the United States. Search by city, state, or 50-mile radius before you adopt, foster, or rehome. Sourced from TBI, Henrico VA, Suffolk NY and verified court convictions."
        path="/"
        jsonLd={[homeJsonLd, orgJsonLd]}
      />
      <HomeRescueBanner />
      <HomeCampaignSection />
      {/* HERO */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-red-600 bg-red-50 text-red-700 px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest font-bold">
              <ShieldAlert size={12} /> Public Records · U.S. Coverage
            </div>
            <h1
              className="font-display font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter mt-6 leading-[0.92]"
              data-testid="hero-title"
            >
              Know who
              <br />
              <span className="relative inline-block">
                <span className="relative z-10">cannot be trusted</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-red-600/90 -z-0" />
              </span>
              <br />
              with an animal.
            </h1>
            <p className="mt-8 text-lg text-zinc-700 max-w-xl leading-relaxed">
            Paws on Record aggregates convicted animal abusers from public
            government registries (Tennessee TBI, Henrico County VA, Suffolk
            County NY), MSU Animal Legal & Historical Center appellate cases
            (TX, CA, FL and more), and widely reported news convictions.
            Search by your city to see who lives within 50 miles.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#near-me" className="btn-primary" data-testid="hero-near-cta">
                <Target size={14} /> Search Near Me
              </a>
              <Link to="/report" className="btn-danger" data-testid="hero-report-cta">
                <FileWarning size={14} /> Report Abuse
              </Link>
              <Link to="/nearby" className="btn-outline" data-testid="hero-nearby-cta">
                <MapPinned size={14} /> Find Help Nearby
              </Link>
              <Link to="/donate" className="btn-outline" data-testid="hero-donate-cta">
                <Heart size={14} /> Donate
              </Link>
            </div>

            {stats && (
              <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 border-t border-zinc-900">
                <div className="border-r border-zinc-900 py-5 pr-5">
                  <div className="label-eyebrow">Convicted Registry</div>
                  <div className="font-display font-black text-4xl mt-1" data-testid="stat-convicted">
                    {stats.convicted_registry_total ?? "—"}
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-1 uppercase tracking-widest">
                    Convicted · Guilty Plea · Registered
                  </div>
                </div>
                <div className="border-r border-zinc-900 py-5 px-5">
                  <div className="label-eyebrow text-amber-700">Pending Cases</div>
                  <div className="font-display font-black text-4xl mt-1" data-testid="stat-pending">
                    {stats.pending_total ?? "—"}
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-1 uppercase tracking-widest">
                    Charged · Indicted · On Trial
                  </div>
                </div>
                <div className="border-r border-zinc-900 py-5 lg:px-5 lg:pl-5 pr-5 pt-5">
                  <div className="label-eyebrow">States Covered</div>
                  <div className="font-display font-black text-4xl mt-1" data-testid="stat-states">
                    {stats.by_state?.length || 0}
                  </div>
                </div>
                <div className="py-5 pl-5">
                  <div className="label-eyebrow">Crime Categories</div>
                  <div className="font-display font-black text-4xl mt-1">
                    {stats.by_crime?.length || 0}
                  </div>
                </div>
              </div>
            )}

            <ShareRegistryButton />

            {/* Species strip */}
            <div className="mt-6 border-t border-zinc-300 pt-4">
              <div className="label-eyebrow mb-2">Animals covered — not just dogs & cats</div>
              <div className="flex flex-wrap gap-1.5" data-testid="species-strip">
                {[
                  "Dogs", "Cats", "Kittens", "Horses", "Cattle", "Livestock",
                  "Pit bulls", "Poodles", "Roosters", "Hens", "Chickens",
                  "Guinea pigs", "Rabbits", "Turtles",
                ].map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1 border border-zinc-900 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest font-bold"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative border border-zinc-900">
              <img
                src={HERO_IMG}
                alt="A healthy rescue dog and cat sitting together in a shelter — a portrait of the animals this registry exists to protect"
                className="w-full h-[520px] object-cover"
                style={{ objectPosition: "right center" }}
                loading="eager"
              />
              <div className="absolute inset-0 bg-zinc-900/15 mix-blend-multiply" />
              <div className="absolute left-4 top-4 bg-white border border-zinc-900 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold">
                <Activity size={10} className="inline mr-1" /> Public Records
              </div>
              <div className="absolute left-4 right-4 bottom-4 bg-zinc-900 text-white p-4 border border-red-600">
                <div className="label-eyebrow text-red-500">Every record matters</div>
                <div className="font-display font-bold text-lg mt-1">
                  Fostering & adoption decisions should begin here.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 🚨 ACTIVE CASES — HELP IDENTIFY (hidden if no open cases) */}
      <ActiveCasesStrip />

      {/* Cash App / Zelle direct-tip anchor target for #donate-tip.
          Same exact Cash App + Zelle rails as /donate — reused verbatim so
          the DONATE NOW button in "Why this matters" (and the top-nav
          Donate link) can jump directly to it without leaving the page. */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-10" data-testid="home-donate-tip-section">
        <CashAppZelleTip />
      </section>

      {/* PURPOSE BANNER — Adoption / Foster screening */}
      <section
        className="border-b border-zinc-900 bg-red-600 text-white"
        data-testid="adoption-purpose-banner"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-2 flex justify-center lg:justify-start">
            <div className="w-20 h-20 border-2 border-white flex items-center justify-center bg-red-700">
              <PawPrint size={38} strokeWidth={2} />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] font-bold text-red-100">
              Before you hand over a leash
            </div>
            <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tighter mt-2 leading-tight">
              Use this site to check a person's background before you give an
              animal for adoption or foster.
            </h2>
            <p className="mt-3 text-sm sm:text-base text-red-50 max-w-2xl leading-relaxed">
              Shelters, rescues, breeders, and private rehomers — search a
              prospective adopter's name, run a 50-mile radius check on their
              city, and confirm they don't appear on a public abuser registry
              before you sign the paperwork.
            </p>
          </div>
          <div className="lg:col-span-3 flex flex-col gap-3">
            <a
              href="#near-me"
              className="bg-white text-zinc-900 font-bold uppercase tracking-wider text-xs px-5 py-3 text-center border-2 border-white hover:bg-zinc-100 inline-flex items-center justify-center gap-2"
              data-testid="banner-radius-cta"
            >
              <Target size={14} /> Check Near Their City
            </a>
            <a
              href="#registry"
              className="border-2 border-white text-white font-bold uppercase tracking-wider text-xs px-5 py-3 text-center hover:bg-white hover:text-zinc-900 inline-flex items-center justify-center gap-2"
              data-testid="banner-name-cta"
            >
              <Search size={14} /> Search By Name
            </a>
          </div>
        </div>
      </section>

      {/* Convicted faces carousel — rotates fully-adjudicated records with photos */}
      <ConvictedCarousel />

      {/* Rescue Partners featured strip — compact teaser under the registry area */}
      <RescuePartnersStrip />


      {/* DONATION IMPORTANCE — keep the registry alive */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-12" data-testid="home-donation-mission">
        <div className="border-2 border-zinc-900 bg-amber-50 p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-9">
            <div className="label-eyebrow flex items-center gap-2 text-red-600">
              <Heart size={11} className="fill-red-600" /> Why this matters
            </div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-2 leading-tight">
              This registry runs on donations — please help keep it free and public.
            </h2>
            <p className="mt-3 text-sm sm:text-base text-zinc-800 max-w-3xl leading-relaxed">
              Paws on Record is a one-person operation that pays out-of-pocket for hosting, the AI news ingestor that
              tracks new convictions, geocoding, email alerts, and legal review. <strong>Every $5 keeps the database
              live for another day; every $100 sponsors an entire week.</strong> If even a fraction of the shelters,
              fosters, and adoption-minded families who use this site donated once, we'd never have to put a single
              record behind a paywall — and that's the goal: protect every animal, free for every searcher, forever.
            </p>
          </div>
          <div className="lg:col-span-3 flex flex-col gap-2">
            <a
              href="#donate-tip"
              className="bg-zinc-900 text-white font-bold uppercase tracking-wider text-xs px-5 py-3 text-center hover:bg-red-600 transition-colors inline-flex items-center justify-center gap-2"
              data-testid="home-donate-cta"
            >
              <Heart size={14} className="fill-white" /> Donate now
            </a>
            <Link
              to="/where-your-money-goes"
              className="text-center text-xs font-mono uppercase tracking-widest text-zinc-700 hover:text-red-600"
              data-testid="home-donate-why-link"
            >
              Where the money goes →
            </Link>
          </div>
        </div>
      </section>


      {/* PRINTABLE LEAD MAGNET */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mt-12" data-testid="home-subscribe-section">
        <SubscribeForm variant="card" source="home_hero" />
      </section>


      {/* NEAR ME */}
      <section id="near-me" className="max-w-7xl mx-auto px-6 lg:px-8 pt-20">
        <div className="mb-5">
          <div className="label-eyebrow">Section · 01 · Radius</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
            Who lives near you?
          </h2>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">
            Enter your city and we'll show convicted animal abusers within the
            chosen radius. Powered by OpenStreetMap geocoding.
          </p>
        </div>
        <NearMeSearch
          filters={filters}
          setFilters={setFilters}
          onSearch={onSearch}
          loading={loading}
        />
        {radiusActive && origin && (
          <div className="mt-4 border border-zinc-300 bg-white px-5 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="radius-result-banner">
            <div className="text-sm">
              <span className="label-eyebrow mr-2">Showing</span>
              <strong>{total}</strong> records within{" "}
              <strong>{filters.radius_mi || 50} miles</strong> of{" "}
              <span className="font-mono">{origin.display_name || `${filters.near_city}${filters.near_state ? ", " + filters.near_state : ""}`}</span>
            </div>
          </div>
        )}
      </section>

      {/* REGISTRY */}
      <section
        id="registry"
        className="max-w-7xl mx-auto px-6 lg:px-8 py-20"
        data-testid="registry-section"
      >
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="label-eyebrow">Section · 02</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
              The Registry
            </h2>
            <p className="text-sm text-zinc-600 mt-2 max-w-xl">
              Filter by name, city, state, crime type or conviction year.
            </p>
            <div className="mt-4">
              <NotifyAlertsButton />
            </div>
          </div>
          {stateStats.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="top-states-row">
              <span className="label-eyebrow w-full mb-1">Top jurisdictions</span>
              {stateStats.map((s) => (
                <button
                  key={s.state_code}
                  onClick={() =>
                    setFilters((f) => ({ ...DEFAULT_FILTERS, state: s.state_code }))
                  }
                  className="border border-zinc-900 px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest hover:bg-zinc-900 hover:text-white transition-colors"
                  data-testid={`top-state-${s.state_code}`}
                >
                  {s.state_code} · {s.count}
                </button>
              ))}
            </div>
          )}
        </div>

        <RegistryFilters
          filters={filters}
          setFilters={setFilters}
          onSearch={onSearch}
          total={total}
        />

        {/* Disposition tabs — Convicted Registry vs Pending vs All */}
        <div className="mt-5 grid grid-cols-3 border-2 border-zinc-900" role="tablist" data-testid="disposition-tabs">
          {[
            { key: "convicted_registry", label: "Convicted Registry", sub: "Convicted · Guilty Plea · Registered", count: stats?.convicted_registry_total },
            { key: "pending", label: "Pending Case Tracker", sub: "Charged · Indicted · On Trial", count: stats?.pending_total },
            { key: "all", label: "All Public Records", sub: "Combined view", count: (stats?.convicted_registry_total || 0) + (stats?.pending_total || 0) },
          ].map((t) => {
            const active = filters.disposition === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setFilters((f) => ({ ...f, disposition: t.key, page: 1 }))}
                className={`px-4 py-3 text-left border-r border-zinc-900 last:border-r-0 transition-colors ${
                  active ? "bg-zinc-900 text-white" : "bg-white text-zinc-900 hover:bg-zinc-100"
                }`}
                data-testid={`disposition-tab-${t.key}`}
                role="tab"
                aria-selected={active}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="label-eyebrow">{t.label}</span>
                  {t.count !== undefined && (
                    <span className={`font-mono font-black text-lg leading-none ${active ? "text-white" : "text-zinc-900"}`}>
                      {t.count}
                    </span>
                  )}
                </div>
                <div className={`text-[10px] uppercase tracking-widest font-mono mt-1 ${active ? "text-zinc-300" : "text-zinc-500"}`}>
                  {t.sub}
                </div>
              </button>
            );
          })}
        </div>

        <SpeciesFilterRow
          filters={filters}
          setFilters={setFilters}
        />

        <CrimeTypeFilterRow
          filters={filters}
          setFilters={setFilters}
        />

        <ReptileProtectionGap filters={filters} />

        {/* Verification legend */}
        <div
          className="mt-6 flex items-center gap-3 flex-wrap text-[11px] font-mono text-zinc-600"
          data-testid="verification-legend"
        >
          <span className="inline-flex items-center gap-1">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="m9 12 2 2 4-4"/>
            </svg>
            <span className="uppercase tracking-widest text-green-700 font-bold">Verified</span>
            <span>= from an official registry, court, or prosecutor</span>
          </span>
          <span className="hidden sm:inline text-zinc-300">·</span>
          <span>other records are sourced from journalism</span>
        </div>

        {loading ? (
          <div className="py-24 text-center label-eyebrow" data-testid="registry-loading">
            Loading records...
          </div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center border border-dashed border-zinc-300 mt-8" data-testid="registry-empty">
            <div className="label-eyebrow">No records match</div>
            <div className="font-display font-bold text-xl mt-2">
              {radiusActive
                ? `Nobody convicted within ${filters.radius_mi || 50} miles of that city.`
                : "Try adjusting your filters."}
            </div>
            {radiusActive && (
              <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
                That's encouraging — but absence from this registry doesn't mean
                nobody in the area has committed a cruelty offense. Official
                state and county registries may have additional data.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
            <div
              className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
              data-testid="registry-grid"
            >
              {items.map((a, i) => (
                <AbuserCard key={a.id} abuser={a} idx={i} />
              ))}
            </div>
            <div className="lg:sticky lg:top-24 lg:self-start">
              <SponsorSidebar />
            </div>
          </div>
        )}

        {total > items.length && items.length > 0 && (
          <div className="flex justify-center mt-12">
            <button
              onClick={() => setFilters((f) => ({ ...f, page: (f.page || 1) + 1 }))}
              className="btn-outline"
              data-testid="registry-next-page"
            >
              Load Next Page
            </button>
          </div>
        )}

        {/* Subtle coverage callout */}
        {!loading && stats?.total && (
          <div className="mt-16 pt-8 border-t border-zinc-200 text-center" data-testid="home-coverage-callout">
            <Link
              to="/about#coverage-map"
              className="inline-flex flex-wrap items-center gap-2 text-sm font-mono text-zinc-600 hover:text-red-600 transition-colors max-w-full"
              data-testid="home-coverage-link"
            >
              <span className="font-bold text-zinc-900">{stats.convicted_registry_total ?? "—"}</span> convicted ·
              <span className="font-bold text-amber-700 ml-1">{stats.pending_total ?? "—"}</span> pending across
              <span className="font-bold text-zinc-900 ml-1">{stats.by_state?.length || 50}</span> states
              <span aria-hidden="true">·</span>
              <span className="underline">See coverage map →</span>
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
